import { useState, useMemo } from "react";
import { useAppData } from "../../lib/AppDataContext";
import { useAuth } from "../../lib/AuthContext";
import { PageHeader } from "../../components/shared/PageHeader";
import { Card } from "../../components/shared/Card";
import { Modal } from "../../components/shared/Modal";
import { BlogPostForm } from "./BlogPostForm";
import { BLOG_STATUS_LABEL, BLOG_STATUS_COLOR } from "../../lib/blogApi";

export function BlogView() {
  const { posts: blogPosts, postsLoaded: blogPostsLoaded, addPost: addBlogPost, updatePost: updateBlogPost, deletePost: deleteBlogPost } = useAppData();
  const { user } = useAuth();
  const role = user?.role || "employee";

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null);

  const filtered = useMemo(() => {
    return blogPosts.filter((p) => {
      if (statusFilter !== "all" && p.status !== statusFilter) return false;
      if (search) {
        const s = search.toLowerCase();
        return p.title.toLowerCase().includes(s) || p.slug.includes(s) || (p.excerpt || "").toLowerCase().includes(s);
      }
      return true;
    });
  }, [blogPosts, search, statusFilter]);

  return (
    <div>
      <PageHeader accent="content"
        title="Blog"
        subtitle={`${filtered.length} of ${blogPosts.length} posts · ${blogPosts.filter((p) => p.status === "published").length} published`}
        actions={
          role !== "employee" && (
            <button
              onClick={() => setAdding(true)}
              className="px-4 py-2 bg-navy text-white rounded text-sm font-medium hover:bg-navy/90"
            >
              + New post
            </button>
          )
        }
      />

      <Card className="overflow-hidden">
        <div className="p-3 sm:p-4 border-b border-slate-200 flex flex-wrap gap-2 items-center">
          <input
            placeholder="Search by title, slug, excerpt…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="flex-1 min-w-[140px] px-3 py-1.5 border border-slate-300 rounded text-sm"
          />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-1.5 border border-slate-300 rounded text-sm"
          >
            <option value="all">All statuses</option>
            {Object.entries(BLOG_STATUS_LABEL).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
        </div>

        {!blogPostsLoaded ? (
          <div className="p-8 text-center text-sm text-slate-500">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-500">
            {blogPosts.length === 0 ? "No posts yet — click \"New post\" to write one." : "No posts match the current filters."}
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {filtered.map((p) => (
              <div
                key={p.id}
                onClick={() => role !== "employee" && setEditing(p)}
                className={`p-3 sm:p-4 ${role !== "employee" ? "cursor-pointer hover:bg-slate-50" : ""}`}
              >
                <div className="flex items-start gap-3">
                  {p.heroImageUrl && (
                    <img
                      src={p.heroImageUrl}
                      alt=""
                      className="w-16 h-16 sm:w-20 sm:h-20 object-cover rounded shrink-0"
                      onError={(e) => { e.target.style.display = "none"; }}
                    />
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-2 mb-1 flex-wrap">
                      <h3 className="font-semibold text-slate-900 leading-tight">{p.title}</h3>
                      <div className="flex items-center gap-1.5 shrink-0">
                        {p.featured && (
                          <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 border border-amber-200 text-[10px] font-medium">★ Featured</span>
                        )}
                        <span className={`px-2 py-0.5 rounded text-[10px] font-medium border ${BLOG_STATUS_COLOR[p.status]}`}>
                          {BLOG_STATUS_LABEL[p.status]}
                        </span>
                      </div>
                    </div>
                    <p className="text-xs text-slate-500 line-clamp-2 mb-1">{p.excerpt || "—"}</p>
                    <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-slate-400">
                      <span>by {p.author}</span>
                      <span>· {p.readingTime} min read</span>
                      <span>· {p.viewCount} views</span>
                      <span>· /blog/{p.slug}</span>
                      {p.category && (
                        <span className="text-slate-500">· {p.category}</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {adding && (
        <Modal onClose={() => setAdding(false)} title="New Blog Post" size="xl" dismissOnBackdrop={false}>
          <BlogPostForm
            onCancel={() => setAdding(false)}
            onSave={async (payload) => {
              await addBlogPost(payload);
              setAdding(false);
            }}
          />
        </Modal>
      )}

      {editing && (
        <Modal onClose={() => setEditing(null)} title="Edit Blog Post" size="xl" dismissOnBackdrop={false}>
          <BlogPostForm
            initial={editing}
            onCancel={() => setEditing(null)}
            onDelete={async () => {
              if (!confirm(`Delete "${editing.title}"? Reversible by super_admin.`)) return;
              await deleteBlogPost(editing.id);
              setEditing(null);
            }}
            onSave={async (payload) => {
              await updateBlogPost({ ...payload, id: editing.id });
              setEditing(null);
            }}
          />
        </Modal>
      )}
    </div>
  );
}
